import { useCallback, useEffect, useRef, useState } from 'react'

import {
  SEARCH_ACTIONS,
  SearchAction,
} from 'shared/components/SearchHeader/SearchHeader'

interface UseSearchHeaderStateProps {
  externalValue: string
  onChange: (value: string) => void
}

// values are exposed as refs, because DataGrid `renderHeader` is memoized together with the columns
export const useSearchHeaderState = ({
  externalValue,
  onChange,
}: UseSearchHeaderStateProps) => {
  const [value, setValue] = useState(externalValue)
  const [active, setActive] = useState(Boolean(externalValue))

  const valueRef = useRef(value)
  const activeRef = useRef(active)

  valueRef.current = value
  activeRef.current = active

  useEffect(() => {
    setValue(externalValue)
    setActive(Boolean(externalValue))
  }, [externalValue])

  const handleChange = useCallback(
    (nextValue: string) => {
      setValue(nextValue)
      onChange(nextValue)
    },
    [onChange]
  )

  const handleToggle = useCallback((action?: SearchAction) => {
    if (action === SEARCH_ACTIONS.OFF) {
      setActive(false)
      return
    }

    setActive(current => !current)
  }, [])

  const handleClear = useCallback(() => {
    setValue('')
    setActive(false)
    onChange('')
  }, [onChange])

  return {
    valueRef,
    activeRef,
    handleChange,
    handleToggle,
    handleClear,
  }
}
